// src/components/AnalysisSection.tsx
import React, { memo, useCallback, useState, useEffect } from "react";
import { Patient, TranscriptionData } from "../types";
import AnalysisSections from "./AnalysisSections";
import { useTheme } from "../contexts/ThemeContext";
import { ChevronDown, ChevronUp, Edit3, Globe, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";


// Supported languages for translation
const SUPPORTED_LANGUAGES: Record<string, string> = {
  'en': 'English',
  'es': 'Spanish', 
  'fr': 'French',
  'de': 'German',
  'hi': 'Hindi',
  'zh': 'Chinese',
  'ja': 'Japanese',
  'ko': 'Korean',
  'pt': 'Portuguese',
  'it': 'Italian',
  'ru': 'Russian',
  'ar': 'Arabic',
  'nl': 'Dutch',
  'pl': 'Polish',
  'tr': 'Turkish',
  'sv': 'Swedish',
  'da': 'Danish',
  'no': 'Norwegian',
  'fi': 'Finnish'
};

interface AnalysisSectionProps {
  claudeApiKey: string;
  transcriptionData: TranscriptionData | null;
  analysis: string;
  processing: boolean;
  patientData: Patient | null;
  editedTranscript: string;
  setEditedTranscript: (value: string) => void;
  statusMsg: string;
  setStatusMsg: (value: string) => void;
  nurseName: string;
  employeeId: string;
  analyzeWithClaude: (transcript: string, patient: Patient, apiKey: string) => void;
  highlightKeywords: (text: string) => string;
  setAnalysis: (value: string) => void;
  onHandoffSubmitted?: () => void;
  handoffType?: string;
  originalLanguage?: string;
}

const AnalysisSection: React.FC<AnalysisSectionProps> = memo(({
  claudeApiKey,
  transcriptionData,
  analysis,
  processing,
  patientData,
  editedTranscript,
  setEditedTranscript,
  statusMsg,
  setStatusMsg,
  nurseName,
  employeeId,
  analyzeWithClaude,
  highlightKeywords,
  setAnalysis,
  onHandoffSubmitted,
  handoffType,
  originalLanguage
}) => {
  const { theme } = useTheme();
  const [submitStatusMsg, setSubmitStatusMsg] = useState<string>("");
  const [isEditingNarrative, setIsEditingNarrative] = useState(false);
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false);
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [isTranslating, setIsTranslating] = useState(false);
  const [originalNarrative, setOriginalNarrative] = useState<string>('');
  const [originalAnalysis, setOriginalAnalysis] = useState<string>('');
  const [translatedNarrative, setTranslatedNarrative] = useState<string>('');
  const [translatedAnalysis, setTranslatedAnalysis] = useState<string>('');
  
  // Detect handoff types
  const isGeneratedHandoff = handoffType === 'Generated by CareShift';
  const isAudioHandoff = handoffType === 'Recorded Audio';
  
  // Track previous handoff type to detect actual changes
  const prevHandoffTypeRef = React.useRef<string>(handoffType);
  
  // Detect when typing is complete for both audio and generated handoffs
  // This runs on mount and whenever analysis/editedTranscript changes
  useEffect(() => {
    if ((isGeneratedHandoff || isAudioHandoff) && analysis && editedTranscript) {
      // Typing is complete when analysis is set (contains emojis) - includes all department emojis with/without variation selectors
      const hasEmojis = /🫀|🫁|🧠|💧|🔬|💊|⚠️?|🚨|⏰|🔍|👨‍⚕️|🏥|😴|📊|🩹|🍽️?|🚶‍♂️|👶|🍼|💉|👪|🎗️?|🧪|🤒|🩺|❤️?|❤|📈|👀|🤰|🩸|🗣️?|🧩|👥|🦾|🏋️?|🛁|📋|🧸|🍎|👨‍👩‍👧/.test(analysis);
      if (hasEmojis) {
        setIsTypingComplete(true);
      }
    }
  }, [isGeneratedHandoff, isAudioHandoff, analysis, editedTranscript]);

  // Reset isTypingComplete when analysis is cleared (new generation starting)
  useEffect(() => {
    if (!analysis) {
      setIsTypingComplete(false);
      setOriginalNarrative('');
      setOriginalAnalysis('');
    }
  }, [analysis]);
  
  // Reset states only when handoff type ACTUALLY changes (not on re-mount)
  useEffect(() => {
    if (prevHandoffTypeRef.current !== handoffType) {
      setIsEditingNarrative(false);
      setIsBreakdownExpanded(false);
      setIsTypingComplete(false);
      setSelectedLanguage('en');
      setOriginalNarrative('');
      setOriginalAnalysis('');
      setTranslatedNarrative('');
      setTranslatedAnalysis('');
      prevHandoffTypeRef.current = handoffType;
    }
  }, [handoffType]);

  // Store original content when typing completes
  useEffect(() => {
    if (isTypingComplete && editedTranscript && analysis && !originalNarrative) {
      setOriginalNarrative(editedTranscript);
      setOriginalAnalysis(analysis);
    }
  }, [isTypingComplete, editedTranscript, analysis, originalNarrative]);

  // Handle language change and translation
  const handleLanguageChange = useCallback(async (langCode: string) => {
    setSelectedLanguage(langCode);
    
    // If switching back to English, restore original content
    if (langCode === 'en') {
      if (originalNarrative) {
        setEditedTranscript(originalNarrative);
        setAnalysis(originalAnalysis);
      }
      setTranslatedNarrative('');
      setTranslatedAnalysis('');
      return;
    }
    
    // Check if we already have this translation cached
    // For now, always translate fresh
    setIsTranslating(true);
    
    try {
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      if (!apiKey) {
        setStatusMsg('OpenAI API key required for translation');
        setIsTranslating(false);
        setSelectedLanguage('en');
        return;
      }
      
      // Get the plain text versions (strip HTML)
      const plainNarrative = (originalNarrative || editedTranscript).replace(/<[^>]*>/g, '');
      const plainAnalysis = (originalAnalysis || analysis).replace(/<[^>]*>/g, '');
      
      // Translate narrative
      const narrativeResponse = await fetch(`${BACKEND_URL}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: plainNarrative,
          target_language: langCode,
          api_key: apiKey
        })
      });
      
      const narrativeData = await narrativeResponse.json();
      
      if (!narrativeData.success) {
        throw new Error(narrativeData.message || 'Translation failed');
      }
      
      // Translate full analysis (includes breakdown)
      const analysisResponse = await fetch(`${BACKEND_URL}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: plainAnalysis,
          target_language: langCode,
          api_key: apiKey
        })
      });
      
      const analysisData = await analysisResponse.json();
      
      if (!analysisData.success) {
        throw new Error(analysisData.message || 'Translation failed');
      }
      
      // Apply highlighting to translated text
      const highlightedNarrative = highlightKeywords(narrativeData.translated_text);
      const highlightedAnalysis = highlightKeywords(analysisData.translated_text);
      
      setTranslatedNarrative(highlightedNarrative);
      setTranslatedAnalysis(highlightedAnalysis);
      setEditedTranscript(highlightedNarrative);
      setAnalysis(highlightedAnalysis);
      
      setStatusMsg(`Displaying in ${SUPPORTED_LANGUAGES[langCode]}`);
      setTimeout(() => setStatusMsg(''), 3000);
      
    } catch (error) {
      console.error('Translation error:', error);
      setStatusMsg('Translation failed. Please try again.');
      setSelectedLanguage('en');
      setTimeout(() => setStatusMsg(''), 3000);
    } finally {
      setIsTranslating(false);
    }
  }, [originalNarrative, originalAnalysis, editedTranscript, analysis, setEditedTranscript, setAnalysis, setStatusMsg, highlightKeywords]);

  // Language selector component
  const LanguageSelector = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0',
      marginBottom: '0.5rem'
    }}>
      <Globe size={16} style={{ color: theme === 'dark' ? '#94a3b8' : '#64748b' }} />
      <span style={{ 
        color: theme === 'dark' ? '#94a3b8' : '#64748b',
        fontSize: '0.9rem',
        fontWeight: '500'
      }}>
        Display in:
      </span>
      <select
        value={selectedLanguage}
        onChange={(e) => handleLanguageChange(e.target.value)}
        disabled={isTranslating}
        style={{
          padding: '0.4rem 0.75rem',
          borderRadius: '6px',
          border: `1px solid ${theme === 'dark' ? '#374151' : '#d1d5db'}`,
          backgroundColor: theme === 'dark' ? '#1e293b' : '#ffffff',
          color: textColor,
          fontSize: '0.9rem',
          cursor: isTranslating ? 'wait' : 'pointer',
          minWidth: '140px'
        }}
      >
        {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
          <option key={code} value={code}>{name}</option>
        ))}
      </select>
      {isTranslating && (
        <Loader2 
          size={16} 
          style={{ 
            color: '#10b981',
            animation: 'spin 1s linear infinite'
          }} 
        />
      )}
    </div>
  );

  // Dynamic colors based on theme
  const textColor = theme === 'dark' ? '#f9fafb' : '#1f2937';
  const backgroundColor = theme === 'dark' ? '#111827' : '#f9f9f9';
  const cardBgColor = theme === 'dark' ? '#121826' : '#ffffff';

  const handleEditedTranscriptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    setEditedTranscript(e.target.value);
  }, [setEditedTranscript]);

  const handleAnalyzeClick = useCallback(() => {
    if (!transcriptionData || !patientData) return;
    const transcript = transcriptionData.translation ?? transcriptionData.transcription;
    analyzeWithClaude(transcript, patientData, claudeApiKey);
  }, [transcriptionData, patientData, claudeApiKey, analyzeWithClaude]);

  const handleSubmitHandoff = useCallback(async () => {
    if (!nurseName || !employeeId) {
      setStatusMsg("⚠️ Please enter nurse name and employee ID first.");
      return;
    }
    if (!analysis.trim()) {
      setStatusMsg("⚠️ No handoff content to submit.");
      return;
    }
    
    try {
      setStatusMsg("📤 Submitting handoff...");
      
      // Always use original English content for storage, regardless of displayed language
      // If user edited while in English (no originalNarrative stored yet), use current content
      const narrativeToSubmit = originalNarrative || editedTranscript;
      const analysisToSubmit = originalAnalysis || analysis;
      
      // Count highlights BEFORE stripping HTML - count </span> (closing tags only) to avoid double-counting
      // This is efficient O(n) string matching, much faster than regex keyword matching or LLM calls
      const countHighlights = (html: string): number => {
        let count = 0;
        let index = 0;
        while ((index = html.indexOf('</span>', index)) !== -1) {
          count++;
          index += 7; // Move past '</span>'
        }
        return count;
      };
      const numHighlights = countHighlights(narrativeToSubmit) + countHighlights(analysisToSubmit);
      
      // Strip HTML tags from the content for clean storage
      const plainNarrativeFull = narrativeToSubmit.replace(/<[^>]*>/g, '');
      const plainAnalysis = analysisToSubmit.replace(/<[^>]*>/g, '');
      
      // Extract the categorical breakdown from the original analysis - includes all department emojis with/without variation selectors
      const emojiPattern = /🫀|🫁|🧠|💧|🔬|💊|⚠️?|🚨|⏰|🔍|👨‍⚕️|🏥|😴|📊|🩹|🤢|🍽️?|🚶‍♂️|👶|🍼|💉|👪|🎗️?|🧪|🤒|🩺|❤️?|❤|📈|👀|🤰|🩸|🗣️?|🧩|👥|🦾|🏋️?|🛁|📋|🧸|🍎|👨‍👩‍👧/;
      const match = plainAnalysis.match(emojiPattern);
      const categoricalBreakdown = match && match.index !== undefined ? plainAnalysis.slice(match.index) : "";
      
      // Extract only the narrative portion (before any category emojis) to avoid duplicates
      const narrativeMatch = plainNarrativeFull.match(emojiPattern);
      const plainNarrative = narrativeMatch && narrativeMatch.index !== undefined 
        ? plainNarrativeFull.slice(0, narrativeMatch.index).trim() 
        : plainNarrativeFull;
      
      // Combine the narrative with the categorical breakdown
      const finalHandoffText = plainNarrative + (categoricalBreakdown ? "\n\n" + categoricalBreakdown : "");
      
      const response = await fetch(`${BACKEND_URL}/handoff/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nurse_name: nurseName,
          nurse_id: employeeId,
          patient_name: patientData?.Name || '',
          patient_mrn: patientData?.MRN || '',
          handoff_text: finalHandoffText,
          handoff_type: handoffType || '',
          original_language: originalLanguage || '',
          num_highlights: numHighlights,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Show local status message below action buttons
        setSubmitStatusMsg("✅ Handoff submitted successfully! You can now select another patient.");
        // Call the callback to reset state instead of reloading the page
        if (onHandoffSubmitted) {
          setTimeout(() => onHandoffSubmitted(), 1500);
        }
      } else {
        setSubmitStatusMsg(`❌ Failed to submit handoff: ${data.message}`);
        setTimeout(() => setSubmitStatusMsg(""), 5000);
      }
    } catch (error) {
      console.error('Error submitting handoff:', error);
      setSubmitStatusMsg("❌ Error submitting handoff. Please try again.");
      setTimeout(() => setSubmitStatusMsg(""), 5000);
    }
  }, [nurseName, employeeId, patientData, analysis, editedTranscript, originalNarrative, originalAnalysis, onHandoffSubmitted, handoffType, originalLanguage, setStatusMsg]);

  const handleDownloadText = useCallback(() => {
    const text = analysis;
    const element = document.createElement("a");
    const file = new Blob([text], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `handoff_analysis_${patientData?.MRN}_${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }, [analysis, patientData?.MRN]);

  const handleCopyAnalysis = useCallback(() => {
    navigator.clipboard.writeText(analysis);
    setStatusMsg("📋 Analysis copied to clipboard.");
    setTimeout(() => setStatusMsg(""), 3000);
  }, [analysis, setStatusMsg]);

  if (!claudeApiKey) {
    return (
      <div className="section-card" style={{ backgroundColor: cardBgColor }}>
        <span style={{ color: textColor }}>Claude API key not configured. Please set up your API key to enable AI analysis.</span>
      </div>
    );
  }

  // Debug logging
  console.log('AnalysisSection state:', {
    isGeneratedHandoff,
    isAudioHandoff,
    processing,
    editedTranscript: editedTranscript ? editedTranscript.substring(0, 50) + '...' : '',
    analysis: analysis ? analysis.substring(0, 50) + '...' : '',
    isTypingComplete,
    handoffType
  });

  // Show processing spinner while waiting for API response (before typing starts)
  if ((isGeneratedHandoff || isAudioHandoff) && processing && !editedTranscript) {
    return (
      <div className="section-card" style={{ 
        backgroundColor: cardBgColor,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        marginTop: 0,
        marginBottom: '1.5rem'
      }}>
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div className="spinner" style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(16, 185, 129, 0.2)',
            borderTop: '4px solid #10b981',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span style={{ color: textColor, fontSize: '0.95rem' }}>
            {statusMsg || 'Processing...'}
          </span>
        </div>
      </div>
    );
  }

  // Show typing animation for both generated and audio handoffs (when typing has started)
  if ((isGeneratedHandoff || isAudioHandoff) && editedTranscript && !isTypingComplete) {
    const headerText = isGeneratedHandoff ? 'Generating Handoff' : 'Analyzing Recording';
    const subText = isGeneratedHandoff 
      ? 'CareShift is creating your handoff narrative...'
      : 'CareShift is analyzing your recorded handoff...';
    
    // Convert markdown bold (**text**) to HTML <strong> tags
    const convertMarkdownBoldTyping = (text: string): string => {
      return text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    };

    // Extract only the narrative portion during typing (before first category emoji)
    const extractNarrativeDuringTyping = (text: string): string => {
      const emojiPattern = /🫀|🫁|🧠|💧|🔬|💊|⚠️?|🚨|⏰|🔍|👨‍⚕️|🏥|😴|📊|🩹|🤢|🍽️?|🚶‍♂️|👶|🍼|💉|👪|🎗️?|🧪|🤒|🩺|❤️?|❤|📈|👀|🤰|🩸|🗣️?|🧩|👥|🦾|🏋️?|🛁|📋|🧸|🍎|👨‍👩‍👧/;
      const match = text.match(emojiPattern);
      if (match && match.index !== undefined) {
        return convertMarkdownBoldTyping(text.slice(0, match.index).trim());
      }
      return convertMarkdownBoldTyping(text);
    };
    
    const typingNarrativeOnly = extractNarrativeDuringTyping(editedTranscript);
    
    return (
      <div style={{ marginTop: 0 }}>
        <div className="analysis-section" style={{ backgroundColor: cardBgColor, marginTop: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '24px',
              height: '24px',
              border: '3px solid rgba(16, 185, 129, 0.2)',
              borderTop: '3px solid #10b981',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              flexShrink: 0
            }} />
            <h4 style={{ color: textColor, margin: 0 }}>{headerText}</h4>
          </div>
          <p style={{ color: textColor, marginLeft: '2.25rem', marginBottom: 0 }}>{subText}</p>
        </div>
        
        {/* Typing animation display with highlighted keywords - shows only narrative portion */}
        <div className="section-card" style={{ backgroundColor: cardBgColor }}>
          <div 
            className="typing-narrative"
            style={{ 
              backgroundColor: backgroundColor,
              padding: '1.5rem',
              borderRadius: '10px',
              lineHeight: '1.8',
              whiteSpace: 'pre-wrap',
              color: textColor,
              minHeight: '200px',
              fontSize: '1rem'
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: typingNarrativeOnly }} />
            <span className="typing-cursor" style={{
              display: 'inline-block',
              width: '2px',
              height: '1.2em',
              backgroundColor: '#10b981',
              marginLeft: '2px',
              animation: 'blink 1s infinite',
              verticalAlign: 'middle'
            }} />
          </div>
        </div>
      </div>
    );
  }

  // Show analysis if it exists
  if (analysis) {
    // Check if analysis has categorical breakdown (emojis) - includes all department emojis
    // Include both with and without variation selectors for emojis that may vary
    const categoryEmojis = ['🫀', '🫁', '🧠', '💧', '🔬', '💊', '⚠️', '⚠', '🚨', '⏰', '🔍', '👨‍⚕️',
                            '🏥', '😴', '📊', '🩹', '🤢', '🍽️', '🍽', '🚶‍♂️', '👶', '🍼', '💉', '👪',
                            '🎗️', '🎗', '🧪', '🤒', '🩺', '❤️', '❤', '📈', '👀', '🤰', '🩸', '🗣️', '🗣', '🧩', '👥',
                            '🦾', '🏋️', '🏋', '🛁', '📋', '🧸', '🍎', '👨‍👩‍👧'];
    const hasCategoricalBreakdown = categoryEmojis.some(emoji => analysis.includes(emoji));
    
    // Convert markdown bold (**text**) to HTML <strong> tags
    const convertMarkdownBold = (text: string): string => {
      return text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    };

    // Extract only the narrative portion (before the first category emoji) for display
    const extractNarrativeOnly = (text: string): string => {
      // Include both with and without variation selectors for emojis that may vary
      const emojiPattern = /🫀|🫁|🧠|💧|🔬|💊|⚠️?|🚨|⏰|🔍|👨‍⚕️|🏥|😴|📊|🩹|🤢|🍽️?|🚶‍♂️|👶|🍼|💉|👪|🎗️?|🧪|🤒|🩺|❤️?|📈|👀|🤰|🩸|🗣️?|🧩|👥|🦾|🏋️?|🛁|📋|🧸|🍎|👨‍👩‍👧|❤/;
      const match = text.match(emojiPattern);
      if (match && match.index !== undefined) {
        return convertMarkdownBold(text.slice(0, match.index).trim());
      }
      return convertMarkdownBold(text);
    };
    
    // Get narrative-only version for display
    const narrativeOnly = extractNarrativeOnly(editedTranscript);
    
    // GENERATED HANDOFF LAYOUT
    if (isGeneratedHandoff) {
      return (
        <div style={{ marginTop: 0 }}>
          <div className="analysis-section" style={{ backgroundColor: cardBgColor, marginTop: 0 }}>
            <h4 style={{ color: textColor, marginTop: 0 }}>Handoff Generated</h4>
            <p style={{ color: textColor, marginBottom: 0 }}>
              Your handoff has been generated with categorical breakdown.
            </p>
          </div>
          
          {/* Language Selector */}
          <div className="section-card" style={{ backgroundColor: cardBgColor, marginBottom: '1rem', padding: '0.75rem 1rem' }}>
            <LanguageSelector />
          </div>
          
          {/* Narrative section with edit button */}
          <div className="section-card" style={{ backgroundColor: cardBgColor, marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: textColor, margin: 0 }}>Handoff Narrative</h3>
              {!isEditingNarrative && (
                <button
                  onClick={() => setIsEditingNarrative(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: theme === 'dark' ? '#374151' : '#e5e7eb',
                    color: textColor,
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Edit3 size={16} />
                  Edit
                </button>
              )}
            </div>
            
            {isEditingNarrative ? (
              <div>
                <textarea
                  value={extractNarrativeOnly(editedTranscript.replace(/<[^>]*>/g, ''))}
                  onChange={handleEditedTranscriptChange}
                  onKeyDown={e => e.stopPropagation()}
                  rows={12}
                  autoComplete="off"
                  style={{ 
                    width: '100%',
                    backgroundColor: theme === 'dark' ? '#1e293b' : '#ffffff', 
                    color: theme === 'dark' ? '#f9fafb' : '#1f2937',
                    borderColor: theme === 'dark' ? '#374151' : '#d1d5db',
                    borderRadius: '8px',
                    padding: '1rem',
                    fontSize: '1rem',
                    lineHeight: '1.6',
                    resize: 'vertical',
                    border: '1px solid'
                  }}
                />
                <button
                  onClick={() => setIsEditingNarrative(false)}
                  style={{
                    marginTop: '0.75rem',
                    padding: '0.5rem 1.5rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  Done Editing
                </button>
              </div>
            ) : (
              <div 
                className="narrative-display"
                style={{ 
                  backgroundColor: backgroundColor,
                  padding: '1.5rem',
                  borderRadius: '10px',
                  lineHeight: '1.8',
                  whiteSpace: 'pre-wrap',
                  color: textColor,
                  fontSize: '1rem'
                }}
                dangerouslySetInnerHTML={{ __html: narrativeOnly }}
              />
            )}
          </div>
          
          {/* Collapsible categorical breakdown */}
          {hasCategoricalBreakdown && (
            <div className="section-card" style={{ backgroundColor: cardBgColor, marginBottom: '1rem' }}>
              <button
                onClick={() => setIsBreakdownExpanded(!isBreakdownExpanded)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: textColor
                }}
              >
                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Categorical Breakdown</h3>
                {isBreakdownExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
              </button>
              
              <div style={{
                maxHeight: isBreakdownExpanded ? '2000px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.4s ease-in-out'
              }}>
                <div style={{ padding: '0 1rem 1rem 1rem' }}>
                  <AnalysisSections analysis={analysis} />
                </div>
              </div>
            </div>
          )}
          
          {/* Submit section */}
            <div className="submit-section" style={{ backgroundColor: cardBgColor }}>
              <h4 style={{ color: textColor, marginBottom: '1rem' }}>Ready to submit your handoff documentation?</h4>
              <button
                className="submit-btn"
                onClick={handleSubmitHandoff}
              >
                Submit Handoff
              </button>
            
              <div className="export-btn-section">
                <button onClick={handleDownloadText}>
                  Download as Text
                </button>
                <button onClick={handleCopyAnalysis}>
                  Copy Analysis
                </button>
                <button onClick={() => window.print()}>
                  Print View
                </button>
              </div>
            </div>
            
            {submitStatusMsg && (
              <div style={{
                padding: '1rem',
                marginTop: '1rem',
                borderRadius: '8px',
                backgroundColor: submitStatusMsg.includes('✅') 
                  ? 'rgba(16, 185, 129, 0.1)'
                  : 'rgba(239, 68, 68, 0.1)',
                border: submitStatusMsg.includes('✅') 
                  ? '1px solid rgba(16, 185, 129, 0.3)'
                  : '1px solid rgba(239, 68, 68, 0.3)',
                color: submitStatusMsg.includes('✅') ? '#10b981' : '#ef4444',
                fontSize: '0.95rem',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {submitStatusMsg}
              </div>
            )}
          </div>
      );
    }
    
    // AUDIO HANDOFF LAYOUT - Same structure as generated handoffs
    return (
      <div style={{ marginTop: 0 }}>
        <div className="analysis-section" style={{ backgroundColor: cardBgColor, marginTop: 0 }}>
          <h4 style={{ color: textColor, marginTop: 0 }}>Analysis Complete</h4>
          <p style={{ color: textColor, marginBottom: 0 }}>
            Your recorded handoff has been analyzed with critical information highlighted.
          </p>
        </div>
        
        {/* Language Selector */}
        <div className="section-card" style={{ backgroundColor: cardBgColor, marginBottom: '1rem', padding: '0.75rem 1rem' }}>
          <LanguageSelector />
        </div>
        
        {/* Narrative section with edit button */}
        <div className="section-card" style={{ backgroundColor: cardBgColor, marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: textColor, margin: 0 }}>Transcribed Narrative</h3>
            {!isEditingNarrative && (
              <button
                onClick={() => setIsEditingNarrative(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: theme === 'dark' ? '#374151' : '#e5e7eb',
                  color: textColor,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                <Edit3 size={16} />
                Edit
              </button>
            )}
          </div>
          
          {isEditingNarrative ? (
            <div>
              <textarea
                value={extractNarrativeOnly(editedTranscript.replace(/<[^>]*>/g, ''))}
                onChange={handleEditedTranscriptChange}
                onKeyDown={e => e.stopPropagation()}
                rows={12}
                autoComplete="off"
                style={{ 
                  width: '100%',
                  backgroundColor: theme === 'dark' ? '#1e293b' : '#ffffff', 
                  color: theme === 'dark' ? '#f9fafb' : '#1f2937',
                  borderColor: theme === 'dark' ? '#374151' : '#d1d5db',
                  borderRadius: '8px',
                  padding: '1rem',
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  resize: 'vertical',
                  border: '1px solid'
                }}
              />
              <button
                onClick={() => setIsEditingNarrative(false)}
                style={{
                  marginTop: '0.75rem',
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                Done Editing
              </button>
            </div>
          ) : (
            <div 
              className="narrative-display"
              style={{ 
                backgroundColor: backgroundColor,
                padding: '1.5rem',
                borderRadius: '10px',
                lineHeight: '1.8',
                whiteSpace: 'pre-wrap',
                color: textColor,
                fontSize: '1rem'
              }}
              dangerouslySetInnerHTML={{ __html: narrativeOnly }}
            />
          )}
        </div>
        
        {/* Collapsible categorical breakdown */}
        {hasCategoricalBreakdown && (
          <div className="section-card" style={{ backgroundColor: cardBgColor, marginBottom: '1rem' }}>
            <button
              onClick={() => setIsBreakdownExpanded(!isBreakdownExpanded)}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: textColor
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Categorical Breakdown</h3>
              {isBreakdownExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
            </button>
            
            <div style={{
              maxHeight: isBreakdownExpanded ? '2000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.4s ease-in-out'
            }}>
              <div style={{ padding: '0 1rem 1rem 1rem' }}>
                <AnalysisSections analysis={analysis} />
              </div>
            </div>
          </div>
        )}
        
        {/* Submit section */}
          <div className="submit-section" style={{ backgroundColor: cardBgColor }}>
            <h4 style={{ color: textColor, marginBottom: '1rem' }}>Ready to submit your handoff documentation?</h4>
            <button
              className="submit-btn"
              onClick={handleSubmitHandoff}
            >
              Submit Handoff
            </button>
          
            <div className="export-btn-section">
              <button onClick={handleDownloadText}>
                Download as Text
              </button>
              <button onClick={handleCopyAnalysis}>
                Copy Analysis
              </button>
              <button onClick={() => window.print()}>
                Print View
              </button>
            </div>
          </div>
          
          {submitStatusMsg && (
            <div style={{
              padding: '1rem',
              marginTop: '1rem',
              borderRadius: '8px',
              backgroundColor: submitStatusMsg.includes('✅') 
                ? 'rgba(16, 185, 129, 0.1)'
                : 'rgba(239, 68, 68, 0.1)',
              border: submitStatusMsg.includes('✅') 
                ? '1px solid rgba(16, 185, 129, 0.3)'
                : '1px solid rgba(239, 68, 68, 0.3)',
              color: submitStatusMsg.includes('✅') ? '#10b981' : '#ef4444',
              fontSize: '0.95rem',
              textAlign: 'center',
              fontWeight: '500'
            }}>
              {submitStatusMsg}
            </div>
          )}
      </div>
    );
  }

  // Note: Processing spinner for generated/audio handoffs is already handled above (lines 362-397)
  // This section is only for other processing states that don't have their own display

  // Show waiting state if transcription exists but no analysis yet (and not processing)
  if (!analysis && transcriptionData) {
    return (
      <div className="section-card" style={{ backgroundColor: theme === 'dark' ? '#121826' : '#ffffff', marginTop: 0 }}>
        <div style={{ 
          textAlign: 'center', 
          padding: '1.5rem',
          color: textColor
        }}>
          <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
            Transcription complete
          </p>
          <p style={{ fontSize: '0.9rem', color: theme === 'dark' ? '#94a3b8' : '#64748b' }}>
            Click "Analyze Handoff" on the left to generate the cleaned narrative and categorical breakdown
          </p>
        </div>
      </div>
    );
  }

  // Empty state when no transcription and no analysis
  return (
    <div className="section-card" style={{ backgroundColor: theme === 'dark' ? '#121826' : '#ffffff', marginTop: 0 }}>
      {/* Empty state - waiting for either transcription or generated handoff */}
    </div>
  );
});

AnalysisSection.displayName = 'AnalysisSection';

export default AnalysisSection;